/**
 * Integration Pattern Selector Component (Step 7)
 *
 * Allows users to select and configure integration patterns for each entity:
 * - Batch ETL: Scheduled extract-transform-load
 * - Real-time CDC: Change Data Capture with Kafka/Debezium
 * - API-based: On-demand API synchronization
 * - Hybrid: Combination of patterns
 *
 * Features:
 * - AI-powered pattern recommendations with confidence scores
 * - Pattern comparison view (pros/cons, cost estimates)
 * - Infrastructure requirements visualization
 * - Execution phase planning with dependency graph
 * - Strategic question coverage tracking
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Clock,
  Zap,
  Globe,
  Layers,
  Server,
  Database,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  HelpCircle,
  RefreshCw,
  Settings,
  Brain,
  DollarSign,
  Cpu,
  HardDrive,
  Network,
  ChevronDown,
  ChevronRight,
  Check,
  X,
  Play,
  Pause,
} from 'lucide-react';
import { Card, CardBody as CardContent, CardHeader } from '../ui/Card';
import Button from '../ui/Button';
import { Badge } from '../ui/Badge';
import Spinner from '../ui/Spinner';
import {
  generateIntegrationPlan,
  getIntegrationPlan,
  getIntegrationPlanSummary,
  getEntityPlans,
  updateEntityPattern,
  getPatternRecommendations,
  getPatternComparison,
  getInfrastructureRequirements,
  getExecutionPhases,
  confirmIntegrationPlan,
  getQuestionCoverage,
} from '../../lib/api/designPhaseApi';
import type {
  IntegrationDesignPlan,
  IntegrationPlanSummary,
  EntityIntegrationPlan,
  PatternRecommendation,
  PatternComparisonResponse,
  InfrastructureRequirements,
  ExecutionPhase,
  IntegrationPatternType,
  IntegrationPatternConfig,
  SLARequirement,
} from '../../types/designPhase';

interface IntegrationPatternSelectorProps {
  caseId: string;
  onConfirm?: (plan: IntegrationDesignPlan) => void;
}

// Pattern icons
const PATTERN_ICONS: Record<IntegrationPatternType, React.FC<{ className?: string }>> = {
  batch_etl: Clock,
  realtime_cdc: Zap,
  api_based: Globe,
  hybrid: Layers,
};

// Pattern colors
const PATTERN_COLORS: Record<IntegrationPatternType, string> = {
  batch_etl: 'bg-blue-100 text-blue-700 border-blue-200',
  realtime_cdc: 'bg-green-100 text-green-700 border-green-200',
  api_based: 'bg-purple-100 text-purple-700 border-purple-200',
  hybrid: 'bg-amber-100 text-amber-700 border-amber-200',
};

// Pattern descriptions
const PATTERN_DESCRIPTIONS: Record<IntegrationPatternType, string> = {
  batch_etl: 'Scheduled data loads at defined intervals (hourly, daily, weekly)',
  realtime_cdc: 'Real-time streaming via Change Data Capture (Debezium + Kafka)',
  api_based: 'On-demand synchronization through REST/GraphQL APIs',
  hybrid: 'Combination of batch, CDC, and API patterns for optimal data flow',
};

// SLA labels
const SLA_LABELS: Record<SLARequirement, string> = {
  real_time: '< 1 minute',
  near_real_time: '1-15 minutes',
  hourly: '1 hour',
  daily: '24 hours',
  weekly: '7 days',
  not_specified: 'Not specified',
};

export const IntegrationPatternSelector: React.FC<IntegrationPatternSelectorProps> = ({
  caseId,
  onConfirm,
}) => {
  const queryClient = useQueryClient();
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
  const [showComparison, setShowComparison] = useState(false);
  const [expandedPhases, setExpandedPhases] = useState<Set<number>>(new Set([0]));
  const [isGenerating, setIsGenerating] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  // Fetch integration plan summary
  const {
    data: planSummary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useQuery({
    queryKey: ['integrationPlanSummary', caseId],
    queryFn: () => getIntegrationPlanSummary(caseId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Fetch full plan
  const { data: fullPlan, isLoading: planLoading } = useQuery({
    queryKey: ['integrationPlan', caseId],
    queryFn: () => getIntegrationPlan(caseId),
    staleTime: 5 * 60 * 1000,
    enabled: !!planSummary,
  });

  // Fetch entity plans
  const { data: entityPlans } = useQuery({
    queryKey: ['entityPlans', caseId],
    queryFn: () => getEntityPlans(caseId),
    staleTime: 5 * 60 * 1000,
    enabled: !!planSummary,
  });

  // Fetch recommendations
  const { data: recommendations } = useQuery({
    queryKey: ['patternRecommendations', caseId],
    queryFn: () => getPatternRecommendations(caseId),
    staleTime: 5 * 60 * 1000,
    enabled: !!planSummary,
  });

  // Fetch infrastructure requirements
  const { data: infrastructure } = useQuery({
    queryKey: ['infrastructure', caseId],
    queryFn: () => getInfrastructureRequirements(caseId),
    staleTime: 5 * 60 * 1000,
    enabled: !!planSummary,
  });

  // Fetch execution phases
  const { data: executionPhases } = useQuery({
    queryKey: ['executionPhases', caseId],
    queryFn: () => getExecutionPhases(caseId),
    staleTime: 5 * 60 * 1000,
    enabled: !!planSummary,
  });

  // Fetch pattern comparison for selected entity
  const { data: comparison, isLoading: comparisonLoading } = useQuery({
    queryKey: ['patternComparison', caseId, selectedEntity],
    queryFn: () => getPatternComparison(caseId, selectedEntity!),
    staleTime: 5 * 60 * 1000,
    enabled: !!selectedEntity && showComparison,
  });

  // Fetch question coverage
  const { data: questionCoverage } = useQuery({
    queryKey: ['questionCoverage', caseId],
    queryFn: () => getQuestionCoverage(caseId),
    staleTime: 5 * 60 * 1000,
    enabled: !!planSummary,
  });

  // Generate plan mutation
  const generateMutation = useMutation({
    mutationFn: () => generateIntegrationPlan(caseId, { include_ai_recommendations: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrationPlanSummary', caseId] });
      queryClient.invalidateQueries({ queryKey: ['integrationPlan', caseId] });
      queryClient.invalidateQueries({ queryKey: ['entityPlans', caseId] });
      queryClient.invalidateQueries({ queryKey: ['patternRecommendations', caseId] });
      queryClient.invalidateQueries({ queryKey: ['infrastructure', caseId] });
      queryClient.invalidateQueries({ queryKey: ['executionPhases', caseId] });
      setIsGenerating(false);
    },
    onError: () => {
      setIsGenerating(false);
    },
  });

  // Update entity pattern mutation
  const updatePatternMutation = useMutation({
    mutationFn: ({
      entityLabel,
      patternType,
    }: {
      entityLabel: string;
      patternType: IntegrationPatternType;
    }) =>
      updateEntityPattern(caseId, entityLabel, {
        entity_label: entityLabel,
        pattern_config: {
          pattern_type: patternType,
          error_handling: {
            retry_count: 3,
            retry_delay_seconds: 60,
            exponential_backoff: true,
            max_retry_delay_seconds: 3600,
            dead_letter_enabled: true,
            dead_letter_retention_days: 30,
            alert_on_failure: true,
            circuit_breaker_enabled: true,
            circuit_breaker_threshold: 5,
            circuit_breaker_timeout_seconds: 300,
          },
          enable_data_quality_checks: true,
          fail_on_quality_errors: false,
          quality_threshold_percent: 95,
          enable_metrics: true,
          metrics_prefix: 'integration',
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entityPlans', caseId] });
      queryClient.invalidateQueries({ queryKey: ['integrationPlan', caseId] });
    },
  });

  // Confirm plan mutation
  const confirmMutation = useMutation({
    mutationFn: () => confirmIntegrationPlan(caseId, { confirmed: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrationPlanSummary', caseId] });
      if (fullPlan) {
        onConfirm?.(fullPlan);
      }
      setIsConfirming(false);
    },
    onError: () => {
      setIsConfirming(false);
    },
  });

  // Handle generate
  const handleGenerate = useCallback(() => {
    setIsGenerating(true);
    generateMutation.mutate();
  }, [generateMutation]);

  // Handle confirm
  const handleConfirm = useCallback(() => {
    setIsConfirming(true);
    confirmMutation.mutate();
  }, [confirmMutation]);

  // Handle pattern change
  const handlePatternChange = useCallback(
    (entityLabel: string, patternType: IntegrationPatternType) => {
      updatePatternMutation.mutate({ entityLabel, patternType });
    },
    [updatePatternMutation]
  );

  // Toggle phase expansion
  const togglePhase = useCallback((phaseNumber: number) => {
    setExpandedPhases((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(phaseNumber)) {
        newSet.delete(phaseNumber);
      } else {
        newSet.add(phaseNumber);
      }
      return newSet;
    });
  }, []);

  // Render pattern badge
  const renderPatternBadge = (patternType: IntegrationPatternType) => {
    const Icon = PATTERN_ICONS[patternType];
    return (
      <Badge className={`flex items-center gap-1 ${PATTERN_COLORS[patternType]}`}>
        <Icon className="w-3 h-3" />
        <span className="capitalize">{patternType.replace('_', ' ')}</span>
      </Badge>
    );
  };

  // Loading state
  if (summaryLoading || planLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Spinner className="w-8 h-8 text-primary-500 mx-auto" />
          <p className="mt-2 text-neutral-600">Loading integration patterns...</p>
        </div>
      </div>
    );
  }

  // No plan yet - show generate button
  if (!planSummary || summaryError) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <Settings className="w-16 h-16 text-neutral-300 mx-auto" />
            <h3 className="mt-4 text-lg font-semibold text-neutral-700">
              No Integration Plan Yet
            </h3>
            <p className="text-sm text-neutral-500 mt-2 max-w-md mx-auto">
              Generate an integration plan to configure how data flows from source systems to your
              Neo4j knowledge graph.
            </p>
            <Button
              onClick={handleGenerate}
              className="mt-6"
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <Spinner className="w-4 h-4 mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Brain className="w-4 h-4 mr-2" />
                  Generate Integration Plan
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with summary */}
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
        <CardContent className="py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-xl font-bold text-neutral-900 flex items-center gap-2">
                <Settings className="w-6 h-6 text-green-600" />
                Integration Pattern Configuration
              </h2>
              <p className="text-sm text-neutral-600 mt-1">
                Primary pattern: {renderPatternBadge(planSummary.primary_pattern)}
              </p>
            </div>

            <div className="flex items-center gap-6">
              {/* Pattern breakdown */}
              <div className="flex items-center gap-4">
                {planSummary.entities_batch_etl > 0 && (
                  <div className="text-center">
                    <div className="text-xl font-bold text-blue-600">
                      {planSummary.entities_batch_etl}
                    </div>
                    <div className="text-xs text-neutral-500">Batch ETL</div>
                  </div>
                )}
                {planSummary.entities_realtime_cdc > 0 && (
                  <div className="text-center">
                    <div className="text-xl font-bold text-green-600">
                      {planSummary.entities_realtime_cdc}
                    </div>
                    <div className="text-xs text-neutral-500">Real-time CDC</div>
                  </div>
                )}
                {planSummary.entities_api_based > 0 && (
                  <div className="text-center">
                    <div className="text-xl font-bold text-purple-600">
                      {planSummary.entities_api_based}
                    </div>
                    <div className="text-xs text-neutral-500">API-based</div>
                  </div>
                )}
              </div>

              <div className="text-center border-l pl-4">
                <div className="text-xl font-bold text-emerald-600">
                  {Math.round(planSummary.coverage_percent)}%
                </div>
                <div className="text-xs text-neutral-500">Question Coverage</div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGenerate}
                  disabled={isGenerating}
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${isGenerating ? 'animate-spin' : ''}`} />
                  Regenerate
                </Button>
                {planSummary.status !== 'confirmed' && (
                  <Button
                    size="sm"
                    onClick={handleConfirm}
                    disabled={isConfirming}
                  >
                    {isConfirming ? (
                      <>
                        <Spinner className="w-4 h-4 mr-1" />
                        Confirming...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Confirm Plan
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pattern selection cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {(Object.keys(PATTERN_ICONS) as IntegrationPatternType[]).map((pattern) => {
          const Icon = PATTERN_ICONS[pattern];
          const isSelected = planSummary.primary_pattern === pattern;
          const count =
            pattern === 'batch_etl'
              ? planSummary.entities_batch_etl
              : pattern === 'realtime_cdc'
              ? planSummary.entities_realtime_cdc
              : pattern === 'api_based'
              ? planSummary.entities_api_based
              : planSummary.entities_hybrid;

          return (
            <Card
              key={pattern}
              className={`cursor-pointer transition-all hover:shadow-md ${
                isSelected ? 'ring-2 ring-primary-500 border-primary-300' : ''
              }`}
            >
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <div
                    className={`p-2 rounded-lg ${
                      isSelected ? 'bg-primary-100' : 'bg-neutral-100'
                    }`}
                  >
                    <Icon
                      className={`w-6 h-6 ${
                        isSelected ? 'text-primary-600' : 'text-neutral-500'
                      }`}
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-neutral-900 capitalize">
                      {pattern.replace('_', ' ')}
                    </h4>
                    <p className="text-xs text-neutral-500 mt-1">
                      {PATTERN_DESCRIPTIONS[pattern]}
                    </p>
                    <div className="mt-2">
                      <Badge variant="outline">
                        {count} entit{count !== 1 ? 'ies' : 'y'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Entity plans table */}
      {entityPlans && entityPlans.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-500" />
              Entity Integration Plans
              <Badge variant="outline" className="ml-2">
                {entityPlans.length} entities
              </Badge>
            </h3>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200">
                    <th className="text-left py-2 px-3 font-medium text-neutral-700">Entity</th>
                    <th className="text-left py-2 px-3 font-medium text-neutral-700">Category</th>
                    <th className="text-left py-2 px-3 font-medium text-neutral-700">Pattern</th>
                    <th className="text-left py-2 px-3 font-medium text-neutral-700">SLA</th>
                    <th className="text-left py-2 px-3 font-medium text-neutral-700">Source</th>
                    <th className="text-left py-2 px-3 font-medium text-neutral-700">Questions</th>
                    <th className="text-left py-2 px-3 font-medium text-neutral-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entityPlans.map((entity) => (
                    <tr
                      key={entity.entity_id}
                      className={`border-b border-neutral-100 hover:bg-neutral-50 ${
                        selectedEntity === entity.entity_label ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td className="py-2 px-3 font-medium text-neutral-900">
                        {entity.entity_label}
                      </td>
                      <td className="py-2 px-3">
                        <Badge variant="outline" className="capitalize text-xs">
                          {entity.data_category.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="py-2 px-3">
                        {renderPatternBadge(entity.pattern.pattern_type)}
                      </td>
                      <td className="py-2 px-3 text-neutral-600">
                        {SLA_LABELS[entity.sla_requirement]}
                      </td>
                      <td className="py-2 px-3 text-neutral-600 truncate max-w-[150px]">
                        {entity.primary_source_system}
                      </td>
                      <td className="py-2 px-3">
                        <Badge variant="outline" className="bg-green-50 text-green-700">
                          {entity.questions_served.length}
                        </Badge>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedEntity(entity.entity_label);
                              setShowComparison(true);
                            }}
                          >
                            <Settings className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pattern comparison panel */}
      {showComparison && selectedEntity && (
        <Card className="border-blue-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-500" />
                Pattern Comparison: {selectedEntity}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowComparison(false);
                  setSelectedEntity(null);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {comparisonLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner className="w-6 h-6" />
              </div>
            ) : comparison ? (
              <div className="grid grid-cols-3 gap-4">
                {(
                  ['batch_etl', 'realtime_cdc', 'api_based'] as IntegrationPatternType[]
                ).map((pattern) => {
                  const data =
                    comparison[pattern as keyof PatternComparisonResponse] as Record<string, unknown>;
                  const isRecommended = comparison.recommended === pattern;
                  return (
                    <div
                      key={pattern}
                      className={`p-4 rounded-lg border-2 ${
                        isRecommended
                          ? 'border-green-500 bg-green-50'
                          : 'border-neutral-200'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        {renderPatternBadge(pattern)}
                        {isRecommended && (
                          <Badge className="bg-green-600 text-white text-xs">Recommended</Badge>
                        )}
                      </div>
                      <div className="space-y-2 text-sm">
                        {Array.isArray(data.pros) && data.pros.length > 0 ? (
                          <div>
                            <p className="text-xs font-medium text-green-600 mb-1">Pros</p>
                            <ul className="text-xs text-neutral-600 space-y-1">
                              {data.pros.slice(0, 3).map((pro: unknown, i: number) => (
                                <li key={i} className="flex items-start gap-1">
                                  <Check className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                                  <span>{String(pro)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                        {Array.isArray(data.cons) && data.cons.length > 0 ? (
                          <div>
                            <p className="text-xs font-medium text-red-600 mb-1">Cons</p>
                            <ul className="text-xs text-neutral-600 space-y-1">
                              {data.cons.slice(0, 2).map((con: unknown, i: number) => (
                                <li key={i} className="flex items-start gap-1">
                                  <X className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" />
                                  <span>{String(con)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                      </div>
                      <Button
                        className="w-full mt-3"
                        size="sm"
                        variant={isRecommended ? 'primary' : 'outline'}
                        onClick={() => handlePatternChange(selectedEntity, pattern)}
                        disabled={updatePatternMutation.isPending}
                      >
                        Select
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Infrastructure requirements */}
      {infrastructure && infrastructure.components.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Server className="w-5 h-5 text-orange-500" />
              Infrastructure Requirements
            </h3>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Cpu className="w-5 h-5 text-blue-500" />
                  <span className="text-sm font-medium">CPU</span>
                </div>
                <p className="text-2xl font-bold text-blue-700 mt-2">
                  {infrastructure.total_cpu_cores} cores
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <HardDrive className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium">Memory</span>
                </div>
                <p className="text-2xl font-bold text-green-700 mt-2">
                  {infrastructure.total_memory_gb} GB
                </p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-purple-500" />
                  <span className="text-sm font-medium">Est. Monthly Cost</span>
                </div>
                <p className="text-2xl font-bold text-purple-700 mt-2">
                  ${infrastructure.estimated_monthly_cost_usd.toLocaleString()}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {infrastructure.requires_kafka && (
                <Badge className="bg-orange-100 text-orange-700">Apache Kafka</Badge>
              )}
              {infrastructure.requires_airflow && (
                <Badge className="bg-cyan-100 text-cyan-700">Apache Airflow</Badge>
              )}
              {infrastructure.requires_kubernetes && (
                <Badge className="bg-indigo-100 text-indigo-700">Kubernetes</Badge>
              )}
              {infrastructure.components.map((comp) => (
                <Badge key={comp.name} variant="outline">
                  {comp.name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Execution phases */}
      {executionPhases && executionPhases.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Play className="w-5 h-5 text-emerald-500" />
              Execution Phases
              <Badge variant="outline" className="ml-2">
                {executionPhases.length} phases
              </Badge>
            </h3>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {executionPhases.map((phase) => (
                <div
                  key={phase.phase_number}
                  className="border rounded-lg overflow-hidden"
                >
                  <div
                    className="flex items-center justify-between p-3 bg-neutral-50 cursor-pointer hover:bg-neutral-100"
                    onClick={() => togglePhase(phase.phase_number)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-bold text-sm">
                        {phase.phase_number}
                      </div>
                      <div>
                        <h4 className="font-medium text-neutral-900">{phase.phase_name}</h4>
                        <p className="text-xs text-neutral-500">{phase.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge variant="outline">
                        {phase.entity_labels.length} entit
                        {phase.entity_labels.length !== 1 ? 'ies' : 'y'}
                      </Badge>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700">
                        ~{phase.estimated_duration_minutes} min
                      </Badge>
                      {expandedPhases.has(phase.phase_number) ? (
                        <ChevronDown className="w-5 h-5 text-neutral-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-neutral-400" />
                      )}
                    </div>
                  </div>
                  {expandedPhases.has(phase.phase_number) && (
                    <div className="p-3 border-t bg-white">
                      <div className="flex flex-wrap gap-2">
                        {phase.entity_labels.map((label) => (
                          <Badge key={label} variant="outline" className="bg-neutral-50">
                            {label}
                          </Badge>
                        ))}
                        {phase.relationship_types.map((type) => (
                          <Badge
                            key={type}
                            variant="outline"
                            className="bg-purple-50 text-purple-700"
                          >
                            {type}
                          </Badge>
                        ))}
                      </div>
                      {phase.depends_on_phases.length > 0 && (
                        <p className="text-xs text-neutral-500 mt-2">
                          Depends on phases: {phase.depends_on_phases.join(', ')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status indicator */}
      {planSummary.status === 'confirmed' && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-medium text-green-800">Integration Plan Confirmed</p>
                <p className="text-sm text-green-600">
                  Ready to proceed with code generation (Step 8)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default IntegrationPatternSelector;
